"use client";

import { formatDistanceToNow } from "date-fns";
import Link from "next/link";
import { Building2, ExternalLink } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SmartContactDisplay } from "@/components/shared/smart-display/SmartContactDisplay";
import type { TabContentProps } from "@/components/shared/UniversalDetailPage/types/detailPage";
import type { Contact_Tasks } from "@/src/gql/graphql";
import { TaskNotesDisplay } from "../components/TaskNotesDisplay";

export function TaskNotesTab({ item, tab, onRefresh }: TabContentProps<Contact_Tasks>) {
  return <TaskNotesDisplay taskId={item.id} showAddButton={true} onRefresh={onRefresh} />;
}

export function TaskActivityTimelineTab({ item }: TabContentProps<Contact_Tasks>) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Communication Timeline</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          <TimelineEvent
            color="bg-blue-500"
            title="Task Created"
            dateText={
              item.created_at
                ? formatDistanceToNow(new Date(item.created_at), { addSuffix: true })
                : "Unknown"
            }
          />

          {item.updated_at && item.created_at && item.updated_at !== item.created_at && (
            <TimelineEvent
              color="bg-yellow-500"
              title="Task Updated"
              dateText={formatDistanceToNow(new Date(item.updated_at), { addSuffix: true })}
            />
          )}

          {item.completed_at && (
            <TimelineEvent
              color="bg-green-500"
              title="Task Completed"
              dateText={formatDistanceToNow(new Date(item.completed_at), { addSuffix: true })}
              className="bg-green-50 dark:bg-green-950/40"
            />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function TimelineEvent({
  color,
  title,
  dateText,
  className = "bg-card-50",
}: {
  color: string;
  title: string;
  dateText: string;
  className?: string;
}) {
  return (
    <div className={`flex items-start gap-3 p-3 rounded-lg ${className}`}>
      <div className={`w-2 h-2 rounded-full mt-2 ${color}`}></div>
      <div>
        <p className="text-sm font-medium text-card-foreground">{title}</p>
        <p className="text-xs text-gray-500">{dateText}</p>
      </div>
    </div>
  );
}

export function ContactInfoTab({ item }: TabContentProps<Contact_Tasks>) {
  if (!item.contacts) {
    return (
      <Card>
        <CardContent>
          <div className="text-center py-8 text-gray-500">
            <Building2 className="h-12 w-12 mx-auto mb-4 text-gray-300" />
            <p>No contact information available</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">Contact Information</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="bg-card-50 p-4 rounded-lg">
            <div className="space-y-3">
              <SmartContactDisplay
                contactId={item.contacts.id}
                contactData={item.contacts}
                fallbackText="Unknown Contact"
                showIcon={true}
                className="text-base"
              />

              <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                <Button asChild variant="outline" className="gap-2">
                  <Link href={`/contacts/${item.contacts.id}`}>
                    <ExternalLink className="h-4 w-4" />
                    View Contact Details
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
